import {
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  Navigate,
} from 'react-router-dom';
import { useReadLocalStorage } from 'usehooks-ts';
import { RootLayout } from '@views/Layout/RootLayout';
import { Login } from '@views/Login';
import { Registration, LegacyRegistration } from '@views/Registration';
import { ForgottenPassword } from '@views/ForgottenPassword';
import { ResetPassword } from '@views/ResetPassword';
import { Profile } from '@views/Profile';
import { Noticeboard } from '@views/Noticeboard';
import { Shipments } from '@views/Shipments';
import { RequireAuth } from '@views/RequireAuth';
import { StockBySKU } from '@views/StockBySKU';
import { ConfirmEmail } from '@views/ConfirmEmail';
import { Team } from '@views/Team';
import { Join } from '@views/Join';
import { PendingApproval } from '@views/PendingApproval';
import { PublicPage } from '@views/PublicPage';
import { WarehouseSearch, WarehouseSearchEmbed } from '@views/WarehouseSearch';
import { WarehouseMaster } from '@views/WarehouseMaster';
import { WarehouseDetails } from '@views/WarehouseDetails';
import { FleetMaster } from '@views/FleetMaster';
import { VehicleDetails } from '@views/VehicleDetails';
import { AddVehicle } from '@views/AddVehicle';
import { PriceMaster } from '@views/PriceMaster';
import { CustomerMaster } from '@views/CustomerMaster';
import { SupplierMaster } from '@views/SupplierMaster';
import { MasterDataImportReview } from '@views/MasterDataImportReview';
import { WarehouseShortlist } from '@views/WarehouseShortlist';
import { AddListing } from '@views/AddListing';
import { CustomerShortlist } from '@views/CustomerShortlist';
import { LegalDocumentsHub } from '@views/LegalDocumentHub';
import { UpdateFinancialInformation } from '@views/UpdateFinancialInformation';
import { InvoiceList } from '@views/Invoice/InvoiceList';
import { Payments } from '@views/Payments';
import { InvoiceDetails } from '@views/Invoice';
import { PayInvoice } from '@views/Invoice/PayInvoice';
import { Payment } from '@views/Payment';
import { UpdatePaymentConsent } from '@views/UpdatePaymentConsent';
import { Transport } from '@views/Transport';
import { CreateWarehouse } from '@views/CreateWarehouse';
import { UpdateWarehouse } from '@views/UpdateWarehouse';
import { AddSupplier } from '@views/AddSupplier';
import { EditSupplier } from '@views/EditSupplier';
import { AddCustomer } from '@views/AddCustomer';
import { EditCustomer } from '@views/EditCustomer';
import { CreateSKU } from '@views/CreateSKU';
import { UpdateSKU } from '@views/UpdateSKU';
import { SKUMaster } from '@views/SKUMaster';
import { EditVehicle } from '@views/EditVehicle';
import { CreateInboundShipmentNote } from '@views/CreateInboundShipmentNote';
import { CreateOutboundShipmentNote } from '@views/CreateOutboundShipmentNote';
import { AddTariff } from '@views/AddTariff';
import { EditTariff } from '@views/EditTariff';
import { AddNetworkRatecard, EditNetworkRatecard } from '@views/AddNetworkRatecard';
import { ClientServiceRates } from '@views/ClientServiceRates';
import { ManagerServiceRates } from '@views/ManagerServiceRates';
import { NotFound } from '@views/NotFound';
import { ErrorBoundary, RouterErrorBoundary } from '@views/ErrorBoundary';
import { CreateTransportOrder } from '@views/CreateTransportOrder';
import { NewOutboundShipment } from '@views/NewOutboundShipment';
import { ShipmentQuote } from '@views/ShipmentQuote';
import { DepotDetails } from '@views/DepotDetails';
import { CreateDepot } from '@views/CreateDepot';
import { UpdateDepot } from '@views/UpdateDepot';
import { Integrations, CompleteEbayConnection } from '@views/Integrations';
import { WingmanBoard } from '@views/WingmanBoard';

function App() {
  const isAuthenticated = useReadLocalStorage('isAuthenticated');

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route
        path='/'
        element={<RootLayout />}
        errorElement={<RouterErrorBoundary />}
      >
        {/* REGISTRATION ROUTES */}
        <Route
          path='/'
          element={
            <Navigate to={isAuthenticated ? '/noticeboard' : '/login'} />
          }
        />
        <Route path='/login' element={<Login />} />
        <Route path='/register' element={<Registration />} />
        <Route path='/register-manually' element={<LegacyRegistration />} />
        <Route path='/forgotten-password' element={<ForgottenPassword />} />
        <Route path='/reset-password' element={<ResetPassword />} />
        <Route path='/confirm-email' element={<ConfirmEmail />} />
        <Route path='/join' element={<Join />} />
        <Route path='/pending-approval' element={<PendingApproval />} />
        {/* END REGISTRATION ROUTES */}

        {/* EMBEDDABLE — no chrome */}
        <Route
          path='/warehouse-search/embed'
          element={<WarehouseSearchEmbed />}
        />

        {/* PUBLIC PAGES */}
        <Route element={<PublicPage />}>
          <Route path='/warehouse-search' element={<WarehouseSearch />} />
          <Route path='/warehouse/:id' element={<WarehouseDetails />} />
          <Route path='/embed' element={<Transport />} />
        </Route>

        {/* PROTECTED ROUTES */}
        <Route element={<RequireAuth />}>
          <Route path='/noticeboard' element={<Noticeboard />} />
          <Route path='/shipments' element={<Shipments />} />

          <Route path='/stock' element={<StockBySKU />} />
          <Route path='/profile' element={<Profile />} />
          <Route
            path='/profile/update-financial-information'
            element={<UpdateFinancialInformation />}
          />
          <Route
            path='/profile/update-payment-consent'
            element={<UpdatePaymentConsent />}
          />
          <Route path='/integrations' element={<Integrations />} />
          <Route
            path='/integrations/ebay/connected'
            element={<CompleteEbayConnection />}
          />
          {/* NOT /wingman — the gateway reverse-proxies /wingman/* to the Wingman service. */}
          <Route path='/wingman-board' element={<WingmanBoard />} />
          <Route path='/team' element={<Team />} />
          <Route path='/warehouse-master' element={<WarehouseMaster />} />
          <Route path='/fleet-master' element={<FleetMaster />} />
          <Route path='/vehicle/add' element={<AddVehicle />} />
          <Route path='/vehicle/:id' element={<VehicleDetails />} />
          <Route path='/vehicle/:id/edit' element={<EditVehicle />} />
          <Route path='/transport-price-master' element={<PriceMaster />} />
          <Route path='/tariff/add' element={<AddTariff />} />
          <Route path='/tariff/:id/edit' element={<EditTariff />} />
          <Route path='/network-ratecard/add' element={<AddNetworkRatecard />} />
          <Route path='/network-ratecard/:id/edit' element={<EditNetworkRatecard />} />
          <Route path='/customer-master' element={<CustomerMaster />} />
          <Route path='/supplier-master' element={<SupplierMaster />} />
          <Route
            path='/master-data/imports/:id'
            element={<MasterDataImportReview />}
          />
          <Route
            path='/fleet-availability'
            element={<Navigate to='/fleet-master' replace />}
          />
          <Route path='/warehouse-shortlist' element={<WarehouseShortlist />} />
          {/* Holly 3.0 Reg spec §6.3 — destination after registration via the LSP-listing journey. */}
          <Route path='/app/listings/add' element={<AddListing />} />
          <Route path='/customers' element={<CustomerShortlist />} />
          <Route path='/legal-documents' element={<LegalDocumentsHub />} />
          <Route path='/invoices' element={<InvoiceList />} />
          <Route path='invoice/pay' element={<PayInvoice />} />
          <Route path='/invoice/:id' element={<InvoiceDetails />} />
          <Route path='/payments' element={<Payments />} />
          <Route path='payment/:id' element={<Payment />} />
          <Route path='/warehouse/create' element={<CreateWarehouse />} />
          <Route path='/warehouse/update/:id' element={<UpdateWarehouse />} />
          <Route
            path='/depot-master'
            element={<Navigate to='/warehouse-master' replace />}
          />
          <Route path='/depot/:id' element={<DepotDetails />} />
          <Route path='/depot/create' element={<CreateDepot />} />
          <Route path='/depot/update/:id' element={<UpdateDepot />} />
          <Route
            path='/warehouse/:warehouseId/service-rates'
            element={<ClientServiceRates />}
          />
          <Route
            path='/warehouse/:warehouseId/manager-service-rates'
            element={<ManagerServiceRates />}
          />
          <Route path='/supplier-master/add' element={<AddSupplier />} />
          <Route path='/supplier-master/edit/:id' element={<EditSupplier />} />
          <Route path='/customer-master/add' element={<AddCustomer />} />
          <Route path='/customer-master/edit/:id' element={<EditCustomer />} />
          <Route path='/sku/create' element={<CreateSKU />} />
          <Route path='/sku/update/:id' element={<UpdateSKU />} />
          <Route path='/sku-master' element={<SKUMaster />} />
          <Route
            path='/inbound-shipment/create'
            element={<CreateInboundShipmentNote />}
          />
          <Route
            path='/despatch-note/create'
            element={<CreateOutboundShipmentNote />}
          />
          <Route
            path='/transport-order/create'
            element={<CreateTransportOrder />}
          />
          <Route
            path='/outbound-shipment/new'
            element={<NewOutboundShipment />}
          />
          <Route path='/shipments/:id/quote' element={<ShipmentQuote />} />
        </Route>
        {/* END PROTECTED ROUTES */}

        {/* 404 CATCH-ALL */}
        <Route path='*' element={<NotFound />} />
      </Route>
    )
  );

  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}

export default App;
